import { DOCUMENT } from "@angular/common";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, tap, catchError, throwError } from "rxjs";
import { environment } from "src/environments/environment.prod";

const TOKEN_KEY = "auth-token";
const USER_KEY = "auth-user";

@Injectable({
  providedIn: "root",
})
export class TokenCookieService {
  private userSubject = new BehaviorSubject<any>(null);
  user$ = this.userSubject.asObservable();

  headers = new HttpHeaders().set("Content-Type", "application/json");
  horizontalPosition: MatSnackBarHorizontalPosition = "end";
  verticalPosition: MatSnackBarVerticalPosition = "top";

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private router: Router,
    private _snackBar: MatSnackBar
  ) {
    // Initialize user from localStorage
    const user = this.getUser();
    if (user) {
      console.log('[TokenCookieService] Initializing with stored user:', user);
      this.userSubject.next(user);
    }
  }

  signOut(refreshToken?: string): Observable<any> {
    const SIGNOUT_URL = `${environment.authApiUrl}/api/v1/auth/logout`;
    console.log('[TokenCookieService] signOut called with refreshToken:', refreshToken);

    const requestParams = new HttpParams().set("refreshToken", refreshToken || '');

    const snackBarRef = this._snackBar.open(
      "Logging out... Please wait.",
      null,
      {
        horizontalPosition: "end",
        verticalPosition: "top",
        panelClass: ["snackbar-success"],
        duration: 1500,
      }
    );

    return this.http
      .put(SIGNOUT_URL, null, {
        params: requestParams,
        observe: "response",
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          console.log('[TokenCookieService] Logout API response:', response);
          this.cleanup();
          snackBarRef.dismiss();
          setTimeout(() => {
            console.log('[TokenCookieService] Navigating to signin and reloading.');
            this.router.navigate(["/signin"]).then(() => {
              location.reload();
            });
          }, 1000);
        }),
        catchError((error) => {
          console.error('[TokenCookieService] Logout API error:', error);
          this.cleanup();
          snackBarRef.dismiss();
          this.router.navigate(["/signin"]).then(() => {
            location.reload();
          });
          return throwError(() => error);
        })
      );
  }

  private cleanup(): void {
    console.log('[TokenCookieService] Cleaning up user data and cookies.');
    this.deleteUser();
    this.clearSharedTokenOrCookie();
    this.clearCookies();
    this.userSubject.next(null);
  }

  public getToken(): string | null {
    const name = "accessToken";
    const cookieArray = document.cookie.split(";");
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(name) === 0) {
        console.log('[TokenCookieService] Retrieved accessToken:', cookie.substring(name.length + 1, cookie.length));
        return cookie.substring(name.length + 1, cookie.length);
      }
    }
    console.warn('[TokenCookieService] No accessToken found.');
    return null;
  }

  public saveUser(user: any): void {
    console.log('[TokenCookieService] Saving user:', user);
    window.localStorage.removeItem(USER_KEY);
    window.localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.userSubject.next(user);
  }

  public deleteUser(): void {
    console.log('[TokenCookieService] Deleting user from localStorage.');
    window.localStorage.removeItem(USER_KEY);
    this.userSubject.next(null);
  }

  public getUser(): any {
    const user = window.localStorage.getItem(USER_KEY);
    if (user) {
      const parsedUser = JSON.parse(user);
      console.log('[TokenCookieService] Retrieved user:', parsedUser);
      return parsedUser;
    }
    console.warn('[TokenCookieService] No user found in localStorage.');
    return null;
  }

  public clearCookies(): void {
    console.log('[TokenCookieService] Clearing all cookies.');
    this.document.cookie.split(";").forEach((c) => {
      this.document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  }

  public setSharedRefreshTokenToCookie(userJSON: string): void {
    console.log('[TokenCookieService] Setting shared refresh token cookie:', userJSON);
    const cookieName = "sharedUserData";
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + 3600 * 1000);
    const cookieValue = `${cookieName}=${encodeURIComponent(
      userJSON
    )}; expires=${expirationDate.toUTCString()}; path=/`;
    document.cookie = cookieValue;
  }

  public getSharedRefreshTokenFromCookie(): string | null {
    const cookieName = "sharedUserData";
    const cookieValue = `; ${document.cookie}`;
    const parts = cookieValue.split(`; ${cookieName}=`);
    if (parts.length === 2) {
      const value = decodeURIComponent(parts.pop().split(";").shift());
      console.log('[TokenCookieService] Retrieved shared refresh token:', value);
      return value;
    }
    console.warn('[TokenCookieService] No shared refresh token found.');
    return null;
  }

  public clearSharedTokenOrCookie(): void {
    console.log('[TokenCookieService] Clearing shared refresh token cookie.');
    const cookieName = "sharedUserData";
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  throwError(): void {
    console.log('[TokenCookieService] Displaying permission error snackbar.');
    this._snackBar.open(
      "You do not have the necessary permissions to access this resource!",
      "X",
      {
        horizontalPosition: this.horizontalPosition,
        verticalPosition: this.verticalPosition,
        duration: 20000,
        panelClass: ["snackbar-danger"],
      }
    );
  }
}